import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import ApiConstants from 'src/app/shared/constants/api.constants';
import { catchError } from 'rxjs/operators';
import {
  UserDetailsResponse,
  UserResponse,
  UserFilterParams,
  UserCallHistoryParams,
  UserCallHistoryResponse,
  UserStatisticsResponse,
} from 'src/app/shared/model/user.model';
import { PreferenceResponse } from 'src/app/shared/model/preference.model';
import { AvailabilityResponse } from 'src/app/shared/model/availability.model';
import { CommonService } from 'src/app/shared/services/common.service';
import { ApiResponse } from 'src/app/shared/model/common.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getUserFilteredData(
    userFilterParams: UserFilterParams,
    withoutCountryUsers?: boolean | null,
    withoutRegionUsers?: boolean | null,
  ): Observable<UserResponse> {
    let API_URL = `${this.apiUrl}${ApiConstants.USER}?PageNumber=${userFilterParams.pageNumber}&PageSize=${userFilterParams.pageSize}`;

    if (
      userFilterParams.name !== null &&
      userFilterParams.name !== '' &&
      userFilterParams.name !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('Name', userFilterParams.name);
    }

    if (
      userFilterParams.regionId !== null &&
      userFilterParams.regionId !== '' &&
      userFilterParams.regionId !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('RegionId', userFilterParams.regionId);
    }

    if (
      userFilterParams.countryId !== null &&
      userFilterParams.countryId !== '' &&
      userFilterParams.countryId !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('CountryId', userFilterParams.countryId);
    }

    if (
      userFilterParams.phoneNumber !== null &&
      userFilterParams.phoneNumber !== '' &&
      userFilterParams.phoneNumber !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('PhoneNumber', userFilterParams.phoneNumber);
    }

    if (
      userFilterParams.createdFrom !== null &&
      userFilterParams.createdFrom !== '' &&
      userFilterParams.createdFrom !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('CreatedFrom', userFilterParams.createdFrom);
    }

    if (
      userFilterParams.createdTo !== null &&
      userFilterParams.createdTo !== '' &&
      userFilterParams.createdTo !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('CreatedTo', userFilterParams.createdTo);
    }

    if (userFilterParams.userType !== null && userFilterParams.userType !== undefined) {
      API_URL += CommonService.getQuerySingleParams('UserType', userFilterParams.userType);
    }

    if (
      userFilterParams.blockedUserStatus !== null &&
      userFilterParams.blockedUserStatus !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams(
        'BlockedUserStatus',
        userFilterParams.blockedUserStatus,
      );
    }

    if (userFilterParams.sortField !== null && userFilterParams.sortField !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortBy', userFilterParams.sortField);
    }

    if (userFilterParams.sortOrder !== null && userFilterParams.sortOrder !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortOrder', userFilterParams.sortOrder);
    }

    if (userFilterParams.nickName !== null && userFilterParams.nickName !== undefined) {
      API_URL += CommonService.getQuerySingleParams('nickname', userFilterParams.nickName);
    }

    if (
      userFilterParams.isExport !== null &&
      userFilterParams.isExport !== false &&
      userFilterParams.isExport !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('isExport', userFilterParams.isExport);
    }

    if (
      userFilterParams.paymentProfileStatus !== null &&
      userFilterParams.paymentProfileStatus !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams(
        'PaymentProfileStatus',
        userFilterParams.paymentProfileStatus,
      );
    }

    if (withoutCountryUsers !== null && withoutCountryUsers !== undefined) {
      API_URL += CommonService.getQuerySingleParams('WithoutCountryUsers', withoutCountryUsers);
    }

    if (withoutRegionUsers !== null && withoutRegionUsers !== undefined) {
      API_URL += CommonService.getQuerySingleParams('WithoutRegionUsers', withoutRegionUsers);
    }

    return this.http
      .get<UserResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getUserData(
    pageNumber: number,
    pageSize: number,
    sortField?: string,
    sortOrder?: string,
  ): Observable<UserResponse> {
    let API_URL = `${this.apiUrl}${ApiConstants.USER}?PageNumber=${pageNumber}&PageSize=${pageSize}`;

    if (sortField !== null && sortField !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortBy', sortField);
    }

    if (sortOrder !== null && sortOrder !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortOrder', sortOrder);
    }

    return this.http
      .get<UserResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateBlockStatus(userId: number, isBlocked: boolean): Observable<ApiResponse<boolean>> {
    let API_URL = `${this.apiUrl}${ApiConstants.USER_DETAILS}${userId}`;
    API_URL += isBlocked ? `${ApiConstants.BLOCK}` : `${ApiConstants.UNBLOCK}`;
    return this.http
      .put<ApiResponse<boolean>>(API_URL, {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getUserDetailsById(userId: string): Observable<UserDetailsResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.USER_DETAILS}${userId}`;
    return this.http
      .get<UserDetailsResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getPreferenceById(userId: string | null, userType: string): Observable<PreferenceResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.PREFERENCE}${userId}?userType=${userType}`;
    return this.http
      .get<PreferenceResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAvailability(userId: string | null): Observable<AvailabilityResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.AVAILABILITY}?userId=${userId}`;
    return this.http
      .get<AvailabilityResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCallHistoryByUserId(callHistoryForUserParams: UserCallHistoryParams) {
    let API_URL = `${this.apiUrl}${ApiConstants.USER_CALL_HISTORY}${callHistoryForUserParams.userId}?PageNumber=${callHistoryForUserParams.pageNumber}&PageSize=${callHistoryForUserParams.pageSize}`;

    if (
      callHistoryForUserParams.createdFrom !== null &&
      callHistoryForUserParams.createdFrom !== '' &&
      callHistoryForUserParams.createdFrom !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams(
        'CreatedFrom',
        callHistoryForUserParams.createdFrom,
      );
    }

    if (
      callHistoryForUserParams.createdTo !== null &&
      callHistoryForUserParams.createdTo !== '' &&
      callHistoryForUserParams.createdTo !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams(
        'CreatedTo',
        callHistoryForUserParams.createdTo,
      );
    }

    if (
      callHistoryForUserParams.userType !== null &&
      callHistoryForUserParams.userType !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('UserType', callHistoryForUserParams.userType);
    }

    if (
      callHistoryForUserParams.sortField !== null &&
      callHistoryForUserParams.sortField !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('SortBy', callHistoryForUserParams.sortField);
    }

    if (
      callHistoryForUserParams.sortOrder !== null &&
      callHistoryForUserParams.sortOrder !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams(
        'SortOrder',
        callHistoryForUserParams.sortOrder,
      );
    }

    return this.http
      .get<UserCallHistoryResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getUserStatisticsData(
    userId: string | null,
    userType: string,
  ): Observable<UserStatisticsResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.USER_STATISTICS_DATA}${userId}?userType=${userType}`;
    return this.http
      .get<UserStatisticsResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
